import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useMemo, useState } from "react";

import {
  useGetChaptersQuery,
  useDeleteChapterMutation,
} from "../../slices/chapterApiSlice";

const ChapterListScreen = () => {
  const navigate = useNavigate();

  const {
    data: chapters = [],
    isLoading,
    error,
    refetch,
  } = useGetChaptersQuery();

  const [deleteChapter, { isLoading: loadingDelete }] =
    useDeleteChapterMutation();
   
   const [selectedSubject, setSelectedSubject] = useState("");
    const subjects = useMemo(() => {
      const uniqueSubjects = [];

      chapters.forEach((chapter) => {
        if (
          chapter.subject &&
          !uniqueSubjects.find((s) => s._id === chapter.subject._id)
        ) {
          uniqueSubjects.push(chapter.subject);
        }
      });

      return uniqueSubjects;
    }, [chapters]);

    const filteredChapters = useMemo(() => {
      if (!selectedSubject) return chapters;

      return chapters.filter(
        (chapter) => chapter.subject?._id === selectedSubject,
      );
    }, [chapters, selectedSubject]);  

  const deleteHandler = async (id, name) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${name}"?`,
    );

    if (!confirmDelete) return;

   

    try {
      await deleteChapter(id).unwrap();

      toast.success("Chapter deleted successfully");

      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Failed to delete chapter",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">
              Question Bank Chapters
            </h1>

            <p className="text-gray-500 mt-2">Manage question bank chapters</p>
          </div>

          <Link
            to="/chapters/create"
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-white font-semibold shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
          >
            + Create Chapter
          </Link>
        </div>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg border border-white/30 shadow-2xl overflow-hidden">
          {isLoading || loadingDelete ? (
            <div className="p-8 text-center text-blue-600 text-lg">
              Loading...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              {error?.data?.message || error?.error}
            </div>
          ) : filteredChapters.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No chapters found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex justify-end mb-4 px-4 pt-4">
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Subjects</option>

                  {subjects.map((subject) => (
                    <option key={subject._id} value={subject._id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-green">
                  <tr>
                    <th className="px-5 py-4 text-left">#</th>

                    <th className="px-5 py-4 text-left">Subject</th>

                    <th className="px-5 py-4 text-left">Chapter</th>

                    <th className="px-5 py-4 text-left">Category</th>

                    <th className="px-5 py-4 text-center">Order</th>

                    <th className="px-5 py-4 text-left">Created By</th>

                    <th className="px-5 py-4 text-center">Status</th>

                    <th className="px-5 py-4 text-center">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredChapters.map((chapter, index) => (
                    <tr
                      key={chapter._id}
                      className="border-b border-gray-200 hover:bg-blue-50 transition"
                    >
                      <td className="px-5 py-4">{index + 1}</td>

                      <td className="px-5 py-4 font-medium">
                        {chapter.subject?.name}
                      </td>

                      <td className="px-5 py-4">{chapter.name}</td>

                      <td className="px-5 py-4">{chapter.subject?.category}</td>

                      <td className="px-5 py-4 text-center">{chapter.order}</td>

                      <td className="px-5 py-4">{chapter.createdBy?.name}</td>

                      <td className="px-5 py-4 text-center">
                        {chapter.isActive ? (
                          <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                            Active
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700">
                            Inactive
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {/* View */}

                          <button
                            onClick={() => navigate(`/chapters/${chapter._id}`)}
                            className="rounded-lg bg-sky-500 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600"
                          >
                            View
                          </button>

                          {/* Edit */}

                          <button
                            onClick={() =>
                              navigate(`/chapters/${chapter._id}/edit`)
                            }
                            className="rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600"
                          >
                            Edit
                          </button>

                          {/* Delete */}

                          <button
                            onClick={() =>
                              deleteHandler(chapter._id, chapter.name)
                            }
                            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChapterListScreen;
